import React from "react";
import Select from "react-select";

export interface DropLocationInputItem {
  id: number | string;
  value: string;
}
interface Props {
  label?: string;
  inputClassName?: string;
  labelClassName?: string;
  containerClassname?: string;
  error?: boolean;
  setInputs: React.Dispatch<React.SetStateAction<DropLocationInputItem[]>>;
  inputs: DropLocationInputItem[];
  generateIds?: boolean;
  /** Add the option the moment it is picked, instead of asking for a second
   *  click on "Add Item". The button stays for the screens that do not opt in. */
  addOnSelect?: boolean;
  options: { value: string; label: string }[];
}

export const DynamicFormInput = ({
  label,
  labelClassName,
  containerClassname,
  error,
  inputs,
  setInputs,
  options,
  generateIds = true,
  addOnSelect = false,
}: Props) => {
  /** State */
  const [selectedOption, setSelectedOption] = React.useState<{
    value: string;
    label: string;
  } | null>(null);

  const addOption = (option: { value: string; label: string } | null) => {
    if (!option) return;
    const newInput = {
      id: generateIds ? inputs?.length + 1 : option?.value,
      value: option?.label ?? "",
    };
    // Picking the same one twice is easy once a single click adds it, and a
    // repeat would duplicate the row and its React key. Compared by id where
    // there is one: two different cities can share a label, two ids cannot.
    setInputs((prev) =>
      prev.some((input) =>
        generateIds
          ? input.value === newInput.value
          : String(input.id) === String(newInput.id)
      )
        ? prev
        : [...prev, newInput]
    );
    setSelectedOption(null);
  };

  const handleAddInput = () => addOption(selectedOption);

  const handleRemoveInput = (id: number | string) => {
    setInputs((prev) => prev.filter((input) => input.id !== id));
  };

  return (
    <div className={`mb-4 ${containerClassname}`}>
      <p
        className={`font-satoshi text-dark text-sm font-medium mb-3 ${labelClassName}`}
      >
        {label}
      </p>
      {inputs.map((input, index) => (
        <div key={input.id} className="flex items-center space-x-2 mb-2">
          <input
            disabled
            type="text"
            value={input.value}
            className={`w-full h-[46px] px-4 bg-sky-lighter border ${
              error ? "border-red-base" : "border-gray-border"
            } focus:border-focused-border rounded font-satoshi text-base text-dark font-medium outline-none`}
            placeholder="Enter item"
          />
          <button
            type="button"
            onClick={() => handleRemoveInput(input.id)}
            className="w-[100px] h-[46px] px-4 bg-red-base text-white rounded font-satoshi text-sm font-medium hover:bg-red-dark"
          >
            Remove
          </button>
        </div>
      ))}

      <div className="flex items-center space-x-2 mb-2">
        <Select
          // Controlled, so the option does not stay in the box after it has
          // been added to the list above.
          value={selectedOption}
          onChange={(option) => {
            if (addOnSelect) {
              addOption(option);
              return;
            }
            setSelectedOption(option);
          }}
          // onInputChange={(value) => setValueToAdd(value)}
          className="w-full"
          styles={{
            control: (baseStyles, state) => ({
              ...baseStyles,
              width: "100%",
              height: "46px",
              padding: "0 16px",
              backgroundColor: "#F2F4F5",
              borderColor: error ? "#FF5247" : "#E2E8F0",
              borderRadius: "4px",
              fontFamily: "satoshi",
              fontSize: "16px",
              color: "var(--dark)",
              fontWeight: 500,
              "&:hover": {
                borderColor: error ? "#FF5247" : "#E2E8F0",
              },
              "&:focus": {
                borderColor: "#64748B",
                boxShadow: "none",
              },
            }),
            placeholder: (baseStyles) => ({
              ...baseStyles,
              color: "#64748B",
            }),
            input: (baseStyles) => ({
              ...baseStyles,
              color: "#202325",
            }),
            option: (baseStyles, state) => ({
              ...baseStyles,
              backgroundColor: state.isSelected ? "#F2F4F5" : "white",
              color: "#202325",
              "&:hover": {
                backgroundColor: "#F2F4F5",
              },
            }),
          }}
          placeholder="Enter item"
          // Already-added options are dropped from the list: with a single
          // click adding them, a repeat pick would otherwise look ignored.
          options={
            addOnSelect
              ? options.filter(
                  (option) =>
                    !inputs.some((input) =>
                      generateIds
                        ? input.value === option.label
                        : String(input.id) === String(option.value)
                    )
                )
              : options
          }
        />
        {addOnSelect ? null : (
          <button
            type="button"
            onClick={handleAddInput}
            disabled={!selectedOption}
            className={`w-[100px] h-[46px] px-4 rounded font-satoshi text-sm font-medium ${
              selectedOption
                ? "bg-blue-500 text-white hover:bg-blue-700"
                : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
          >
            Add Item
          </button>
        )}
      </div>
    </div>
  );
};
